import { Fragment } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import validator from "validator";
import { SP } from "../../http/serviceportalApi";
import { useTranslations } from "../../hooks/useTranslations";
import FormField from "../ui/FormField";
import FormFieldGrid from "../ui/FormFieldGrid";
import Input from "../ui/Input";
import Select from "../ui/Select";
import ToggleSwitch from "../ui/ToggleSwitch";
import styles from "./CustomerRow.module.css";
import { QuestionnaireFormValues } from "./QuestionnaireTab";

const REVISOR_TYPE_OPTIONS: { value: SP.RevisorType }[] = [
  { value: "revisore_contabile" },
  { value: "societa_e_responsabile" },
  { value: "collegio_sindacale" },
];

interface TaxCodeField {
  isOptional: boolean;
  helpText?: string;
}

interface CustomerRowProps {
  customer: SP.Customer;
  index: number;
  disabled: boolean;
  form: UseFormReturn<QuestionnaireFormValues>;
}

const CustomerRow = ({
  customer,
  index: i,
  disabled,
  form,
}: CustomerRowProps) => {
  const { i18n } = useTranslations();
  const {
    register,
    setValue,
    clearErrors,
    control,
    formState: { errors },
  } = form;

  const TAX_CODE_FIELDS: Record<SP.RevisorType, TaxCodeField[]> = {
    revisore_contabile: [{ isOptional: false }],
    societa_e_responsabile: [
      {
        isOptional: false,
        helpText:
          i18n.economicAdvisorApp.questionnaire.revisorTypes
            .societa_e_responsabile.helpTexts.societa,
      },
      {
        isOptional: false,
        helpText:
          i18n.economicAdvisorApp.questionnaire.revisorTypes
            .societa_e_responsabile.helpTexts.responsabile,
      },
    ],
    collegio_sindacale: [
      { isOptional: false },
      { isOptional: false },
      { isOptional: true },
    ],
  };

  const isValidTaxCode = (v: string | undefined) =>
    !v ||
    validator.isTaxID(v, "it-IT") ||
    validator.isVAT(`IT${v}`, "IT") ||
    i18n.economicAdvisorApp.questionnaire.freelanceTaxCode.invalidError;

  const freelanceInvoices = useWatch({
    control,
    name: `customers.${i}.freelanceInvoices`,
  });
  const revisor = useWatch({ control, name: `customers.${i}.revisor` });
  const revisorType = useWatch({
    control,
    name: `customers.${i}.revisorType`,
  });

  const taxCodeFields =
    revisorType && revisorType in TAX_CODE_FIELDS
      ? TAX_CODE_FIELDS[revisorType]
      : [];

  const clearRevisorFields = () => {
    setValue(`customers.${i}.revisorType`, "");
    setValue(`customers.${i}.revisorTaxCodes`, []);
    clearErrors(`customers.${i}.revisorType`);
    clearErrors(`customers.${i}.revisorTaxCodes`);
  };

  const clearRevisorTaxCodes = () => {
    setValue(`customers.${i}.revisorTaxCodes`, []);
    clearErrors(`customers.${i}.revisorTaxCodes`);
  };

  return (
    <Fragment>
      <tr>
        <td align="left" className={styles.customerName}>
          {customer.name}
        </td>
        <td>
          <ToggleSwitch
            {...register(`customers.${i}.freelanceInvoices`, {
              onChange: ({ target }) => {
                if (target.checked) return;
                setValue(`customers.${i}.taxCode`, undefined);
                clearErrors(`customers.${i}.taxCode`);
              },
            })}
            disabled={disabled}
          />
        </td>
        <td>
          <ToggleSwitch
            {...register(`customers.${i}.revisor`, {
              onChange: ({ target }) => {
                if (target.checked) return;
                clearRevisorFields();
              },
            })}
            disabled={disabled}
          />
        </td>
      </tr>
      {freelanceInvoices && (
        <tr className={styles.customerDetail}>
          <td colSpan={3}>
            <FormFieldGrid>
              <FormField
                label={
                  i18n.economicAdvisorApp.questionnaire.freelanceTaxCode.label
                }
                helpText={
                  i18n.economicAdvisorApp.questionnaire.freelanceTaxCode
                    .helpText
                }
                error={errors.customers?.[i]?.taxCode}
              >
                {({ isInvalid }) => (
                  <Input
                    className={styles.uppercase}
                    disabled={disabled}
                    isInvalid={isInvalid}
                    {...register(`customers.${i}.taxCode`, {
                      validate: {
                        format: isValidTaxCode,
                      },
                      required: freelanceInvoices,
                    })}
                  />
                )}
              </FormField>
            </FormFieldGrid>
          </td>
        </tr>
      )}
      {revisor && (
        <tr className={styles.customerDetail}>
          <td colSpan={3}>
            <FormFieldGrid>
              <FormField
                label={
                  i18n.economicAdvisorApp.questionnaire.revisorFunction.label
                }
                error={errors.customers?.[i]?.revisorType}
              >
                {({ isInvalid }) => (
                  <Select
                    disabled={disabled}
                    isInvalid={isInvalid}
                    {...register(`customers.${i}.revisorType`, {
                      required: revisor,
                      onChange: () => clearRevisorTaxCodes(),
                    })}
                  >
                    <option value="">
                      {
                        i18n.economicAdvisorApp.questionnaire.revisorFunction
                          .placeholder
                      }
                    </option>
                    {REVISOR_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {
                          i18n.economicAdvisorApp.questionnaire.revisorTypes[
                            opt.value
                          ].label
                        }
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
              {taxCodeFields.map((field, fieldIndex) => (
                <FormField
                  key={`${revisorType}-${fieldIndex}`}
                  label={
                    taxCodeFields.length > 1
                      ? `${i18n.economicAdvisorApp.questionnaire.revisorTaxCode} ${fieldIndex + 1}`
                      : i18n.economicAdvisorApp.questionnaire.revisorTaxCode
                  }
                  helpText={field.helpText}
                  isOptional={field.isOptional}
                  error={errors.customers?.[i]?.revisorTaxCodes?.[fieldIndex]}
                >
                  {({ isInvalid, isOptional }) => (
                    <Input
                      className={styles.uppercase}
                      disabled={disabled}
                      isInvalid={isInvalid}
                      {...register(
                        `customers.${i}.revisorTaxCodes.${fieldIndex}`,
                        {
                          validate: { format: isValidTaxCode },
                          required: !isOptional,
                        },
                      )}
                    />
                  )}
                </FormField>
              ))}
            </FormFieldGrid>
          </td>
        </tr>
      )}
    </Fragment>
  );
};

export default CustomerRow;
